import React from "react";
import {
  Container,
  Typography,
  Box,
  Divider,
  TextField,
  Button,
  Grid,
  Paper,
  FormControlLabel,
  Checkbox,
  Chip,
} from "@mui/material";
import getClauses from "./data/data";
import useDetails from "./formik/useDetail";
import { toast } from "react-toastify";
import { containsPropertyMerchant } from "utils/index";
import { appendDetails } from "services/MerchantHostApi";
import { useDispatch } from "react-redux";
import { updateInitialState } from "slice/merchantSlice";

const VendorAgreement = () => {
  const dispatch = useDispatch();
  const { formik, userDetails } = useDetails();
  const [loading, setLoading] = React.useState(false);
  const {
    values: {
      executionDate,
      executionStatus,
      readVendorAgreement,
      legalName,
      address,
      authorisation,
    },
  } = formik;

  const isSubmitted =
    executionStatus !== "PENDING" && executionStatus !== "REJECTED";

  const clauses = getClauses(userDetails?.userId);

  const checkPropery = () => {
    return (
      Object.hasOwn(userDetails, "organisationDetails") &&
      Object.hasOwn(userDetails, "bankDetails")
    );
  };

  const handleSubmit = async (status: string) => {
    if (!checkPropery()) {
      toast.error("Please complete the KYC process first.");
      return;
    }

    if (!formik.isValid) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    const data: any = {
      vendorAgreement: {
        ...formik.values,
        executionStatus: status,
      },
    };

    const val = containsPropertyMerchant(userDetails, "vendorAgreement");

    if (val === "UPDATE") {
      data.kycStatus = "UPDATE";
    } else if (val === "TRUE") {
      data.kycStatus = "COMPLETED";
    }

    try {
      const response = await appendDetails(
        userDetails?.userType,
        userDetails.userId,
        data
      );
      if (response?.message === "Item updated successfully") {
        toast.success("Merchant details updated successfully!");
        dispatch(updateInitialState(response?.updatedItem));
        window.location.reload();
      }
    } catch (error) {
      toast.error("Something went wrong");
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Notice Section */}
      <Box p={1}>
        <Typography variant="body1" color="red" gutterBottom fontSize={16}>
          *Please note that the vendor agreement will be accepted only if you
          have submitted Merchant KYC, Bank Details and Organisation Details.
        </Typography>
      </Box>
      {/* Agreement Section */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontWeight: "bold", textAlign: "center", mb: 4 }}
        >
          VENDOR AGREEMENT
        </Typography>

        <Typography variant="body1" paragraph>
          This Vendor Agreement (hereinafter referred to as{" "}
          <strong>“Agreement”</strong>) is executed on {executionDate}{" "}
          (hereinafter the <strong>“Effective Date”</strong>) in India.
        </Typography>

        {/* Parties Section */}
        <Box sx={{ my: 2 }}>
          <Typography
            variant="body1"
            gutterBottom
            sx={{ fontWeight: "bold", textAlign: "center" }}
          >
            BY AND BETWEEN
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>KASHIKA1 TECHNOLOGY PRIVATE LIMITED</strong>, having its
            registered office at
            <strong>
              {" "}
              "Bichiya GKP, Jangle Tulshiram, Sadar, Gorakhpur- 273014, Uttar
              Pradesh, India"
            </strong>
            , where such expression shall be taken to mean, unless expressly
            stated to the contrary, its successors and assigns (hereinafter
            referred to as the <strong>"Company"</strong>);
          </Typography>
          <Typography variant="body1" textAlign={"center"}>
            AND
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 1,
              my: 1,
            }}
          >
            <Typography variant="body1">
              <TextField
                variant="standard"
                placeholder="Legal Name"
                value={legalName}
                sx={{ width: 400, marginRight: 2 }}
                disabled
                InputProps={{
                  style: { fontWeight: "bold", fontSize: 16 },
                }}
              />
              having its registered office at
              <TextField
                variant="standard"
                placeholder="Address"
                value={address}
                fullWidth
                sx={{ fontSize: 12 }}
                disabled
                InputProps={{
                  style: { fontWeight: "bold", fontSize: 16 },
                }}
              />
              where such expression shall be taken to mean, unless expressly
              stated to the contrary, its successors and assigns (hereinafter
              referred to as the <strong>“Vendor”</strong>);
            </Typography>
          </Box>
          <Typography variant="body1" textAlign={"justify"} paragraph>
            The “Company” and the “Vendor” shall be referred to” individually as
            “Party” and as “Parties” collectively, as the context may require.
          </Typography>

          {/* Clauses Section */}
          <Typography fontWeight={600} textAlign={"justify"} marginBottom={1}>
            RECITALS:
          </Typography>
          <Typography variant="body1" textAlign={"justify"} paragraph>
            <strong>WHEREAS</strong>, Kashika1 Technology Private Limited, doing
            business as Kozyclan, operates a dynamic digital marketplace
            designed to facilitate bookings for a broad spectrum of travel and
            hospitality services including group and community travel
            experiences, bespoke travel adventures, hostel accommodations,
            experiential stays, event tickets, and community gatherings and
            provides a platform where independent vendors can list and offer
            their services;
          </Typography>
          <Typography variant="body1" textAlign={"justify"} paragraph>
            <strong>WHEREAS</strong>, Kozyclan has developed and continues to
            enhance its platform to ensure a seamless, secure, and efficient
            user experience for both service providers and consumers, thereby
            supporting the growth and success of its vendors while ensuring high
            standards of quality and customer satisfaction;
          </Typography>
          <Typography variant="body1" textAlign={"justify"} paragraph>
            <strong>WHEREAS</strong>, the Vendor desires to register on the
            Kozyclan platform to market and provide its travel and hospitality
            services to a wide customer base, acknowledging that it will offer
            accurate, complete, and up-to-date service descriptions, maintain
            high service quality, and comply with all platform policies, pricing
            guidelines, and regulatory requirements as set forth by Kozyclan;
          </Typography>
          <Typography variant="body1" textAlign={"justify"} paragraph>
            <strong>WHEREAS</strong>, both Parties recognize the mutual benefits
            of establishing a legally binding arrangement that clearly outlines
            their respective rights, responsibilities, and obligations,
            including the management of bookings, payment processing, commission
            structures, intellectual property rights, cancellation and penalty
            policies, and procedures for termination or suspension of services;
          </Typography>
          <Typography variant="body1" textAlign={"justify"} paragraph>
            <strong>NOW, THEREFORE,</strong> in consideration of the mutual
            covenants and promises contained herein, and for other good and
            valuable consideration, the receipt and sufficiency of which are
            hereby acknowledged, the Parties agree to be bound by the terms and
            conditions of this Vendor Agreement.
          </Typography>
          {/* End of Clauses Section */}
          {clauses?.map((clause, index) => (
            <React.Fragment key={index}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "1rem",
                  marginTop: 2,
                  marginBottom: 1,
                }}
              >
                {clause?.id}. {clause?.title}
              </Typography>
              <Box sx={{ marginLeft: 2, fontFamily: "Inter" }}>
                {clause?.data}
              </Box>
            </React.Fragment>
          ))}
        </Box>
        <Divider sx={{ my: 3 }} />
        <Typography variant="body1" textAlign={"justify"} paragraph>
          <strong>IN WITNESS WHEREOF</strong>, each of the undersigned has duly
          executed this Agreement (or caused this Vendor Agreement to be
          executed on its behalf by its officer or representative thereunto duly
          authorized)
        </Typography>

        <Box
          sx={{
            mt: 6,
            p: 4,
            border: `1px solid black`,
            borderRadius: 2,
          }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                For Company
              </Typography>
              <TextField
                fullWidth
                value={"Harsh Bhushan"}
                disabled
                margin="normal"
              />
              <TextField
                fullWidth
                value={"Director"}
                disabled
                margin="normal"
              />
              <TextField
                fullWidth
                value={"Gorakhpur"}
                disabled
                margin="normal"
              />
              <TextField
                fullWidth
                value={executionDate}
                disabled
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                For Vendor
              </Typography>
              <TextField
                fullWidth
                label="Name"
                value={formik.values.authorisation?.name || ""}
                onChange={(e) =>
                  formik.setFieldValue("authorisation.name", e.target.value)
                }
                disabled={isSubmitted}
                margin="normal"
              />

              <TextField
                fullWidth
                label="Designation"
                value={authorisation.designation}
                onChange={(e) =>
                  formik.setFieldValue(
                    "authorisation.designation",
                    e.target.value
                  )
                }
                disabled={isSubmitted}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Place"
                value={authorisation.Place}
                onChange={(e) =>
                  formik.setFieldValue("authorisation.Place", e.target.value)
                }
                disabled={isSubmitted}
                margin="normal"
              />
              <TextField
                fullWidth
                disabled
                value={executionDate}
                margin="normal"
              />
            </Grid>
          </Grid>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={readVendorAgreement}
              onChange={(e) =>
                formik.setFieldValue("readVendorAgreement", e.target.checked)
              }
              disabled={isSubmitted}
            />
          }
          label="I have read and understood all the points mentioned above in the agreement."
        />

        <Box
          sx={{
            display: isSubmitted ? "flex" : "none",
            justifyContent: "center",
            my: 2,
          }}
        >
          <Chip
            label={executionStatus}
            color={executionStatus === "ACCEPTED" ? "success" : "error"}
            sx={{ fontWeight: 600, fontSize: 16 }}
          />
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            mt: 3,
            display: isSubmitted ? "none" : "flex",
            justifyContent: "center",
            gap: 4,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ px: 6, py: 1.5 }}
            disabled={loading || !readVendorAgreement}
            onClick={() => handleSubmit("ACCEPTED")}
          >
            Accept Agreement
          </Button>
          <Button
            variant="contained"
            color="error"
            size="large"
            sx={{ px: 6, py: 1.5 }}
            disabled={loading || !readVendorAgreement}
            onClick={() => handleSubmit("REJECTED")}
          >
            Decline
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default VendorAgreement;
